/**
 * 订单详情抽屉模块 - 纯函数与样式映射
 *
 * 集中存放订单状态/支付状态/审核状态的样式映射常量，
 * 以及时间徽章计算、到达状态计算、日期格式化等纯函数（付款状态判定在 `utils/paymentAmount.ts`）。
 * 全部为无副作用的模块级定义，供主组件与各 Section 复用。
 */

/** 订单执行状态徽章样式映射 */
export const ORDER_STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  '未执行': { bg: 'bg-amber-50',  text: 'text-amber-600', dot: 'bg-amber-400' },
  '执行中': { bg: 'bg-cyan-50',   text: 'text-cyan-600',  dot: 'bg-cyan-400' },
  '已完成': { bg: 'bg-teal-50',   text: 'text-teal-600',  dot: 'bg-teal-400' },
  '已取消': { bg: 'bg-rose-50',   text: 'text-rose-500',  dot: 'bg-rose-400' },
  '已归档': { bg: 'bg-gray-100',  text: 'text-gray-500',  dot: 'bg-gray-300' },
};

/**
 * 支付状态徽章样式映射：键就是后端 `payment_status` 的三个契约值。
 * 不设「已退款」——这一列没有该值（PG 枚举只有未付款/部分付款/已结清），退款只在会员订单与支付流水上。
 * 也不设「无金额」：0 元占位单的徽章由 `hasNoPaymentObligation` 整块撤下，不靠第四个档位兜。
 */
export const PAYMENT_STATUS_STYLES: Record<string, { bg: string; text: string; icon: string }> = {
  '已结清':   { bg: 'bg-teal-50 border-teal-200',     text: 'text-teal-600',  icon: 'ri-checkbox-circle-line' },
  '部分付款': { bg: 'bg-sky-50 border-sky-200',       text: 'text-sky-600',   icon: 'ri-wallet-3-line' },
  '未付款':   { bg: 'bg-orange-50 border-orange-200', text: 'text-red-700',   icon: 'ri-error-warning-line' },
};

/** 审核状态徽章样式映射 */
export const REVIEW_STATUS_STYLES: Record<string, { bg: string; text: string; icon: string; label: string }> = {
  '待审核': { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-600', icon: 'ri-shield-line',       label: '待审核' },
  '已通过': { bg: 'bg-teal-50 border-teal-200',   text: 'text-teal-600',  icon: 'ri-shield-check-line', label: '已通过' },
  '已拒绝': { bg: 'bg-rose-50 border-rose-200',   text: 'text-rose-500',  icon: 'ri-shield-cross-line', label: '已拒绝' },
};

/**
 * 审核终态：对方已经给出结论（通过或拒绝）。
 * 详情抽屉据此决定渲染形态——终态只留徽章行那一枚徽标，不再渲染整张审核面板；
 * 「待审核」还在等人动作，面板（按钮 + 违约风险提示）必须留着。
 */
export const isReviewSettled = (status: string | null | undefined): boolean =>
  status === '已通过' || status === '已拒绝';

/** 日期格式化为 YYYY-MM-DD（无效/空值返回 '-'） */
export const fmtDate = (d: string | null | undefined) => {
  if (!d) return '-';
  const dt = new Date(d);
  return dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0') + '-' + String(dt.getDate()).padStart(2, '0');
};

/**
 * 时刻格式化为 YYYY-MM-DD HH:mm（本机时区）。无效/空值返回**空串**而不是 '-'：
 * 调用方是「原因 · 姓名 · 时间」这类拼接，缺一段就该少一段，不该多出「· -」。
 *
 * 与 fmtDate 分开是因为量纲不同：这里进的是带 Z 的完整时刻（审计行的 createdAt），
 * `new Date` 按本机时区展开正是想要的；档期那些 `YYYY-MM-DD` 纯日期绝不能这么解析，
 * 会被当成 UTC 零点、东八区以外整体漂一天。
 */
export const fmtDateTime = (d: string | null | undefined) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  const day = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  return `${day} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
};

/** 根据预约开始日期计算日程徽章（今日/明日/N 天后/已过期） */
export function calcScheduleBadge(startDate: string): { label: string; style: string; icon: string; pulse: string } | null {
  if (!startDate) return null;
  const start = new Date(startDate + 'T00:00:00');
  const todayD = new Date();
  todayD.setHours(0, 0, 0, 0);
  const diff = Math.round((start.getTime() - todayD.getTime()) / 86400000);
  if (diff === 0) return { label: '今日活动', style: 'bg-rose-100 text-rose-600 border border-rose-200', icon: 'ri-alarm-line', pulse: 'bg-rose-400' };
  if (diff === 1) return { label: '明日活动', style: 'bg-orange-100 text-orange-600 border border-orange-200', icon: 'ri-time-line', pulse: 'bg-orange-400' };
  if (diff > 1 && diff <= 7) return { label: diff + ' 天后', style: 'bg-amber-100 text-amber-600 border border-amber-200', icon: 'ri-calendar-2-line', pulse: '' };
  if (diff > 7) return { label: diff + ' 天后', style: 'bg-gray-100 text-gray-500 border border-gray-200', icon: 'ri-calendar-2-line', pulse: '' };
  return { label: '已过期 ' + Math.abs(diff) + ' 天', style: 'bg-gray-100 text-gray-400 border border-gray-200', icon: 'ri-history-line', pulse: '' };
}

/** 根据到达时间与预约时间计算到达状态（提前/准时/延迟） */
export function calcArrivalStatus(
  arrivalDateTime: string | null,
  startDate: string | null,
  startTime: string | null,
  isFullDay: boolean | null
): { label: string; style: string; icon: string; diffMin: number } | null {
  if (!arrivalDateTime || !startDate) return null;
  const arr = new Date(arrivalDateTime);

  let targetTime: Date;
  if (!isFullDay && startTime) {
    const [h, m] = startTime.split(':').map(Number);
    targetTime = new Date(startDate + 'T00:00:00');
    targetTime.setHours(h, m, 0, 0);
  } else {
    // 全天默认比较 09:00
    targetTime = new Date(startDate + 'T09:00:00');
  }

  const diffMin = Math.round((targetTime.getTime() - arr.getTime()) / 60000);

  if (diffMin > 30) {
    return {
      label: `提前 ${diffMin >= 60 ? Math.floor(diffMin / 60) + '小时' + (diffMin % 60 > 0 ? diffMin % 60 + '分' : '') : diffMin + '分钟'}`,
      style: 'bg-teal-50 text-teal-600 border-teal-200',
      icon: 'ri-time-line',
      diffMin,
    };
  }
  if (diffMin >= -15) {
    return {
      label: '准时到达',
      style: 'bg-emerald-50 text-emerald-600 border-emerald-200',
      icon: 'ri-checkbox-circle-line',
      diffMin,
    };
  }
  const absDiff = Math.abs(diffMin);
  return {
    label: `延迟 ${absDiff >= 60 ? Math.floor(absDiff / 60) + '小时' + (absDiff % 60 > 0 ? absDiff % 60 + '分' : '') : absDiff + '分钟'}`,
    style: 'bg-rose-50 text-rose-500 border-rose-200',
    icon: 'ri-alarm-warning-line',
    diffMin,
  };
}

/**
 * 活动地址的坐标导航：终点直接落位，用户不用在地图里再搜一遍。
 *
 * `to` 是「经度,纬度,名称」，`coordinate=gaode` 声明这一对坐标就是高德自己的 GCJ-02，
 * `callnative=1` 让装了客户端的手机尝试唤醒 App，没装则留在网页版。
 * 与关键词检索那条（mapSearchUrl）的分工由调用方的三档逻辑决定：有坐标走这条，
 * 解析不出坐标才检索。参数与 Flutter `core/utils/map_nav.dart` 的同名候选逐字一致。
 */
export const mapNavigationUrl = (lat: number, lon: number, name: string) =>
  `https://uri.amap.com/navigation?to=${lon},${lat},${encodeURIComponent(name)}&mode=car&policy=1&src=mypage&coordinate=gaode&callnative=1`;

/**
 * 活动地址的关键词检索兜底：把地址当检索词丢给高德 URI API。
 *
 * 只在拿不到坐标时用（后台未配置高德 Key、地址太含糊、第三方超时）。
 * 高德这一条 URL 在装了客户端的手机上会尝试唤醒 App（callnative=1），没装则留在网页版。
 * 同步镜像：`app_flutter/lib/core/utils/map_nav.dart`、
 * `hmos/entry/src/main/ets/pages/OrderDetailPage.ets`。
 */
export const mapSearchUrl = (address: string) =>
  `https://uri.amap.com/search?keyword=${encodeURIComponent(address)}&callnative=1`;
